import { Spinner } from '@forest/shared/solid'
import { Show, type Accessor } from 'solid-js'
import coinIcon from '~/assets/currency/coin.svg'
import gemIcon from '~/assets/currency/gem.svg'
import type { CatalogueCurrency } from '~/lib/catalogue-price'
import { intlLocale } from '~/lib/params'
import type { DailyPicksRefreshState } from '~/store/daily-picks-refresh'
import type { DailyPicksRefreshTap } from '~/store/daily-picks-refresh-flow'
import { canAfford, type ShopBalances } from '~/store/session-ledger'
import { RefreshIcon } from './icons/refresh'

const numberFormatter = new Intl.NumberFormat(intlLocale)

// The Daily Picks refresh button (GLOSSARY.md "Daily Picks refresh"): the
// current refresh tier's currency and price on a CTA pill beside the section
// heading, and the tap that opens the Confirm Refresh dialog. Nothing renders
// until the refresh count has loaded, nor while the tickets or count are
// unavailable; the heading text sets the row's height either way. While a
// refresh is in flight the pill spins and is disabled — that disabled button
// is the flow's single-flight lock (ADR 0032).
//
// A tier the session ledger cannot cover follows the tree rule (GLOSSARY.md
// "Insufficient purchase balance"): the price renders in the unaffordable
// tint and the tap navigates straight to Coins & Crystals for that currency,
// with no confirmation and no request.
//
// In the exhausted pool (GLOSSARY.md) and in the last minute before the
// rollover (GLOSSARY.md "Last-minute refresh lock") the pill dims but stays
// tappable — the tap is how the user learns that a redraw has nothing new
// to deal, or that the same rotation is seconds away for free, so a
// disabled or vanished pill that explains nothing would be the wrong shape.
// Either dim yields to the pending spinner, which has more to say while a
// refresh is running.
export function DailyPicksRefreshButton(props: {
  balances: Accessor<ShopBalances | undefined>
  // True in the exhausted pool (GLOSSARY.md): every unowned good left is
  // already on the shelf. Judged over the grid on screen, so the button
  // carries it into the tap for the flow to answer.
  exhausted: boolean
  // True while the flow's lock holds, which the Shop server clock lifts of
  // its own accord as the rollover carries it past midnight.
  locked: boolean
  onRefresh: DailyPicksRefreshTap
  onTopUp: (currency: CatalogueCurrency) => void
  pending: boolean
  state: DailyPicksRefreshState
}) {
  return (
    <Show when={props.state.status === 'ready' && props.state}>
      {(ready) => {
        const affordable = () =>
          canAfford(props.balances(), {
            currency: ready().tier.currency,
            price: ready().tier.price,
          })
        return (
          // The spinner is narrower than any tier's price: the `min-w`
          // floor keeps the pill's width for the length of the refresh.
          <button
            type="button"
            data-testid="daily-picks-refresh"
            data-amount={ready().tier.price}
            data-currency={ready().tier.currency}
            disabled={props.pending}
            // The exhausted pool and the lock are answered ahead of the
            // shortage: sending someone to buy currency for a redraw that
            // deals nothing new, or for a rotation moments away for free,
            // is the very thing each exists to prevent, so such a tap goes
            // to the flow either way and the flow explains itself.
            onClick={() =>
              props.exhausted || props.locked || affordable()
                ? props.onRefresh(ready().tier, {
                    exhausted: props.exhausted,
                  })
                : props.onTopUp(ready().tier.currency)
            }
            class="flex h-10 min-w-[100px] shrink-0 items-center justify-center gap-0.5 rounded-full bg-cta px-4 text-base font-bold text-white"
            classList={{
              'opacity-50': (props.exhausted || props.locked) && !props.pending,
            }}
          >
            <Show
              when={!props.pending}
              fallback={<Spinner class="size-[22px] text-white" />}
            >
              <RefreshIcon />
              <img
                alt=""
                class="size-4"
                src={ready().tier.currency === 'coin' ? coinIcon : gemIcon}
              />
              <span
                data-testid="daily-picks-refresh-price"
                data-affordable={affordable() ? 'true' : 'false'}
                classList={{ 'text-[#F88F8E]': !affordable() }}
              >
                {numberFormatter.format(ready().tier.price)}
              </span>
            </Show>
          </button>
        )
      }}
    </Show>
  )
}
